import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Search, MapPin, User, Receipt, Handshake, Phone, ShieldCheck, X, CalendarDays, Link2, PencilLine, Trash2, Building2, Loader2 } from "lucide-react";
import { PageHeader, Spinner, StatusBadge, EmptyState } from "../../components/common";
import { GpsPhotoGallery } from "../../components/GpsPhotoGallery";
import { CorrectedBadge, DemandEditDialog } from "../../components/DemandEditDialog";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { AuditTimeline } from "../../components/AuditTimeline";
import { api, fmtDateTime, fmtINR, fmtDateIST, fmtDay, getAuth } from "../../lib/api";
import { AreaLinkageDialog } from "../../components/AreaLinkageDialog";
import { useTerritoryMasters, TerritoryFilterSelects, namesOf } from "../../components/TerritoryFilter";
import { StaffLabel } from "../../components/StaffAvatar";
import { CollectionSummaryCards, PaymentHistoryTable, ResponsibleStaff } from "../../components/PropertyCollectionSummary";
import { VisitTimeline, useVisitHistory } from "../../components/VisitTimeline";
import { DrilldownBanner, useUrlFilters } from "../../components/DrilldownBanner";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, useFetchGate } from "../../components/FetchBar";
import { MobileNumberHistory, fmtMobile } from "../../components/MobileVerifyGate";
import { PropertyRegisterSummary } from "../../components/PropertyRegisterSummary";
import { PropertyExportButton } from "../../components/PropertyExportButton";
import { DeletePropertyDialog, BLOCKED_RECEIPTS_MSG } from "../../components/DeletePropertyDialog";   // Phase 192
import { DeletedPropertiesTab } from "../../components/DeletedPropertiesTab";                         // Phase 192
import { useLiveCollections } from "../../lib/liveCollections";
import { useRoleModules } from "../../lib/roleModules";

// Phase 84: register filters live in the URL so dashboard drilldowns can deep-link
// (?ward_id= / ?source=) and the filtered view is shareable. `focus` (Phase 65 PIN
// jump) is a one-shot param and is stripped once consumed.
// Phase 140: + mohalla and the "date added" range (IST days) — shared by the list, the summary cards and the export.
const FILTER_KEYS = ["search", "zone_id", "ward_id", "mohalla_id", "payment_status", "source", "added_from", "added_to"];
const STRIP_KEYS = ["focus"];

// property source registry: label + how prominent the row badge is
export const SOURCE_META = {
  excel_upload: { label: "Excel Upload" },
  manual_entry: { label: "Manual Entry" },
  new_survey: { label: "Survey" },
  reassessment: { label: "Reassessment" },
  third_party: { label: "Third Party", highlight: true },
};

export function SourceBadge({ source }) {
  const meta = SOURCE_META[source];
  if (!meta?.highlight) return null;
  return (
    <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-800 border-violet-200 gap-0.5"
      data-testid="third-party-badge">
      <Handshake className="h-2.5 w-2.5" /> {meta.label}
    </Badge>
  );
}

// Phase 90: who / what / when / why history of one property (lazy-loaded on tab open)
export function PropertyAuditTrail({ code, propId }) {
  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    if (!propId) return;
    setLoading(true); setError(null);
    api.get(`/t/${code}/properties/${propId}/audit-trail`)
      .then((r) => setTrail(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load the audit trail"))
      .finally(() => setLoading(false));
  }, [code, propId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="property-audit-trail">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" /> Permanent record of every collection, assessment, approval and edit — including by staff who have since left.
        </p>
        {trail && <Badge variant="secondary" className="shrink-0" data-testid="property-audit-count">{trail.total} event{trail.total === 1 ? "" : "s"}</Badge>}
      </div>
      {error ? (
        <div className="text-sm text-destructive flex items-center justify-between gap-2" data-testid="property-audit-error">
          <span>{error}</span><Button size="sm" variant="outline" onClick={load}>Retry</Button>
        </div>
      ) : (
        <AuditTimeline code={code} events={trail?.events} loading={loading} emptyText="No recorded activity for this property yet." />
      )}
    </div>
  );
}

export function PropertyDetailSheet({ code, propId, onClose, canDelete = false, onDeleted }) {
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("details");
  const { history } = useVisitHistory(code, propId);
  // Phase 188: Municipal Admin (and the Super Admin) can correct a demand with a mandatory reason
  const canEditDemand = ["municipal_admin", "super_admin"].includes(getAuth()?.user?.role);
  const [editDemand, setEditDemand] = useState(null);
  const [deleting, setDeleting] = useState(false);   // Phase 192
  const reloadDetail = useCallback(() => {
    if (propId) api.get(`/t/${code}/properties/${propId}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [code, propId]);

  useEffect(() => {
    if (propId) {
      setDetail(null);
      setTab("details");
      setDeleting(false);
      api.get(`/t/${code}/properties/${propId}`).then((r) => setDetail(r.data)).catch(() => {});
    }
  }, [propId, code]);
  // Phase 149: a payment recorded elsewhere refreshes the open sheet's totals + history in place (no spinner)
  useLiveCollections(code, reloadDetail, { enabled: !!propId });
  const hasReceipts = Number(detail?.collection_summary?.payments_count ?? detail?.payments?.length ?? 0) > 0;

  return (
    <Sheet open={!!propId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        {!detail ? <Spinner /> : (
          <>
            <SheetHeader>
              <SheetTitle className="font-heading flex items-center gap-2 flex-wrap pr-6">
                <span className="font-mono text-base">{detail.property.pin}</span>
                <StatusBadge status={detail.property.payment_status} />
                <SourceBadge source={detail.property.source} />
                {/* Phase 192: delete this property (mandatory reason; blocked when receipts exist) */}
                {canDelete && (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-auto">
                          <Button size="sm" variant="outline" className="h-8 text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => setDeleting(true)} data-testid="property-sheet-delete-button">
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-xs max-w-[260px]">
                        {hasReceipts ? `${BLOCKED_RECEIPTS_MSG} — this property has receipts and cannot be deleted.` : "Move this property to the recycle bin (reason required). Only the Super Admin can restore it."}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </SheetTitle>
            </SheetHeader>
            {canDelete && (
              <DeletePropertyDialog open={deleting} onOpenChange={setDeleting} code={code}
                property={{ ...detail.property, collection_summary: detail.collection_summary }}
                onDeleted={(d) => { onDeleted?.(d); onClose(); }} testId="property-sheet-delete" />
            )}
            <Tabs value={tab} onValueChange={setTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-2" data-testid="property-detail-tabs">
                <TabsTrigger value="details" data-testid="property-tab-details">Details</TabsTrigger>
                <TabsTrigger value="audit" data-testid="property-tab-audit" className="gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Audit trail</TabsTrigger>
              </TabsList>
              <TabsContent value="audit" className="mt-4">
                <PropertyAuditTrail code={code} propId={propId} />
              </TabsContent>
              <TabsContent value="details">
            <div className="space-y-5 mt-4" data-testid="property-detail-sheet">
              {/* Phase 104: totals + who is accountable, first thing in the sheet */}
              <div className="space-y-2" data-testid="property-collection-summary-section">
                <CollectionSummaryCards summary={detail.collection_summary} testId="property-collection-summary" />
                <ResponsibleStaff code={code} responsible={detail.responsible} testId="property-responsible" />
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ["Owner", detail.property.owner_name],
                  ["Father/Spouse", detail.property.father_spouse_name],
                  ["House No", detail.property.house_no],
                  ["Zone", detail.property.zone_label],
                  ["Ward", detail.property.ward_label],
                  ["Mohalla", detail.property.mohalla_label],
                  ["Category", detail.property.category_label],
                  ["Property Type", detail.property.property_type_label],
                  ["Usage", detail.property.usage_type_label],
                  ["Construction", detail.property.const_type_label],
                  ["Road Width", detail.property.road_width_label],
                  ["Plot Area", detail.property.total_area_plot_sqft ? `${detail.property.total_area_plot_sqft} sqft` : "-"],
                  ["Source", SOURCE_META[detail.property.source]?.label || detail.property.source || "-"],
                  ["Added on", detail.property.created_at ? fmtDateTime(detail.property.created_at) : "-"],
                ].map(([l, v]) => (
                  <div key={l}>
                    <p className="text-xs text-muted-foreground">{l}</p>
                    <p className="font-medium">{v || "-"}</p>
                  </div>
                ))}
                {/* Phase 140: who added this property (inferred for legacy records) */}
                <div data-testid="property-added-by">
                  <p className="text-xs text-muted-foreground">Added by</p>
                  <p className="font-medium">
                    {detail.property.created_by_name
                      ? <StaffLabel code={code} userId={detail.property.created_by} name={detail.property.created_by_name} />
                      : <span className="text-muted-foreground">—</span>}
                    {detail.property.created_by_inferred && <span className="text-[10px] text-muted-foreground ml-1">(inferred)</span>}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Address</p>
                  <p className="font-medium">{detail.property.address || "-"}</p>
                </div>
                {/* Phase 134: only the LATEST number is shown here; old numbers live in the Number History below */}
                <div className="col-span-2 rounded-lg border bg-muted/40 px-3 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="property-mobile-current">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-primary" aria-hidden="true" />
                    <div>
                      <p className="text-xs text-muted-foreground">Mobile (latest)</p>
                      <p className="font-semibold tabular-nums tracking-wide" data-testid="property-mobile-current-number">{detail.property.mobile ? fmtMobile(detail.property.mobile) : "—"}</p>
                    </div>
                  </div>
                  {detail.property.mobile_updated_at && (
                    <p className="text-[11px] text-muted-foreground" data-testid="property-mobile-updated-meta">
                      Updated {fmtDateTime(detail.property.mobile_updated_at)}{detail.property.mobile_updated_by_name ? ` by ${detail.property.mobile_updated_by_name}${detail.property.mobile_updated_by_employee_id ? ` (${detail.property.mobile_updated_by_employee_id})` : ""}` : ""}
                    </p>
                  )}
                </div>
                {detail.custom_fields.map((cf) => (
                  <div key={cf.label}>
                    <p className="text-xs text-muted-foreground">{cf.label}</p>
                    <p className="font-medium">{cf.value}</p>
                  </div>
                ))}
              </div>

              {/* Phase 134: every old number with who changed it and when — read-only here (only the Super Admin can delete) */}
              <div data-testid="property-mobile-history-section">
                <MobileNumberHistory code={code} propId={propId} testId="property-mobile-history" />
              </div>

              {(detail.property.third_party_photos?.length > 0 || detail.property.property_photos?.length > 0) && (
                <GpsPhotoGallery code={code}
                  photos={[...(detail.property.third_party_photos || []), ...(detail.property.property_photos || [])]} />
              )}

              <div data-testid="property-demands-section">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-sm">Demands</p>
                  {canEditDemand && <p className="text-[11px] text-muted-foreground">Correct a demand with the pencil — a reason is required and the change is audited.</p>}
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Head</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Paid</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead>Status</TableHead>
                        {canEditDemand && <TableHead className="w-10"><span className="sr-only">Edit</span></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.demands.map((d) => (
                        <TableRow key={d.id} data-testid={`property-demand-row-${d.id}`}>
                          <TableCell className="text-sm">
                            <span className="flex items-center gap-1.5 flex-wrap">{d.tax_head_name}<CorrectedBadge demand={d} testId={`property-demand-corrected-${d.id}`} /></span>
                            <p className="text-[11px] text-muted-foreground">{d.fy_label}</p>
                            {(Number(d.penalty_amount) > 0 || Number(d.rebate_amount) > 0) && (
                              <p className="text-[11px] text-muted-foreground tabular-nums" data-testid={`property-demand-adjust-${d.id}`}>
                                {Number(d.penalty_amount) > 0 ? `Penalty ${fmtINR(d.penalty_amount)}` : ""}{Number(d.penalty_amount) > 0 && Number(d.rebate_amount) > 0 ? " · " : ""}{Number(d.rebate_amount) > 0 ? `Rebate ${fmtINR(d.rebate_amount)}` : ""}
                              </p>
                            )}
                            {d.tp_breakdown?.components?.length > 0 && (
                              <div className="mt-1 pl-2 border-l-2 border-border space-y-0.5" data-testid={`demand-breakdown-${d.id}`}>
                                {d.tp_breakdown.components.map((c) => (
                                  <p key={c.key || c.label} className="text-[11px] text-muted-foreground">{c.label}: <span className="tabular-nums">{fmtINR(c.amount)}</span></p>
                                ))}
                                {Math.abs(d.tp_breakdown.adjustment || 0) >= 0.01 && (
                                  <p className="text-[11px] text-muted-foreground italic" data-testid={`demand-breakdown-adjustment-${d.id}`}>Adjustment: <span className="tabular-nums">{fmtINR(d.tp_breakdown.adjustment)}</span></p>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums" data-testid={`property-demand-total-${d.id}`}>{fmtINR(d.total_demand)}</TableCell>
                          <TableCell className="text-right tabular-nums" data-testid={`property-demand-paid-${d.id}`}>{fmtINR(d.paid_amount)}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium" data-testid={`property-demand-balance-${d.id}`}>{fmtINR(d.balance_amount)}</TableCell>
                          <TableCell><StatusBadge status={d.payment_status} /></TableCell>
                          {canEditDemand && (
                            <TableCell className="text-right">
                              <Button size="icon" variant="ghost" className="h-7 w-7" title="Correct this demand (reason required)" onClick={() => setEditDemand(d)}
                                data-testid={`property-demand-edit-${d.id}`}>
                                <PencilLine className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              {canEditDemand && (
                <DemandEditDialog code={code} demand={editDemand} pin={detail.property.pin} open={!!editDemand}
                  onOpenChange={(v) => { if (!v) setEditDemand(null); }} onSaved={reloadDetail} />
              )}

              <div data-testid="property-collection-section">
                <p className="font-medium text-sm mb-2">Collection history</p>
                <PaymentHistoryTable code={code} payments={detail.payments} testId="property-collection-history" />
              </div>

              <div data-testid="property-visit-history-section">
                <p className="font-medium text-sm mb-2">
                  Visit history{history ? ` (${history.summary.total_visits})` : ""}
                </p>
                <VisitTimeline code={code} visits={history?.visits || []} />
              </div>
            </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function Properties() {
  const { code } = useParams();
  const access = useRoleModules(code);   // Phase 152: "properties.export" gates the Excel export button
  // Phase 160: an admin who filtered by ward / mohalla and got nothing is offered the area-linkage check right here
  const isAdmin = getAuth()?.user?.role === "municipal_admin";
  // Phase 192: deleting properties — feature `properties.delete_properties` (OFF by default, granted by the Super Admin).
  // view → the Deleted properties tab; write → + Delete (row, detail sheet, selected rows). Never for supervisors.
  const canSeeDeleted = isAdmin && access.featureAllowed("properties", "delete_properties");
  const canDelete = isAdmin && access.featureLevel("properties", "delete_properties") === "write";
  const [linkageOpen, setLinkageOpen] = useState(false);
  const [data, setData] = useState(null);
  // Phase 176: one set of masters feeds the cascading multi-select Zone / Ward / Mohalla filters
  const masters = useTerritoryMasters(code);
  const { zones, wards, mohallas } = masters;
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const { filters, patch, clear, arrivedFiltered } = useUrlFilters(FILTER_KEYS, { strip: STRIP_KEYS });
  // Phase 133: the register loads only on Fetch (arriving from a dashboard drilldown counts as that click)
  const gate = useFetchGate(filters, { autoFetch: arrivedFiltered });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Phase 192: Register / Deleted properties tabs (deep-linkable with ?tab=deleted) + multi-select for bulk delete
  const view = canSeeDeleted && searchParams.get("tab") === "deleted" ? "deleted" : "register";
  const setView = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "deleted") next.set("tab", "deleted"); else next.delete("tab");
    setSearchParams(next, { replace: true });
  };
  const [checked, setChecked] = useState({});          // {property_id: row}
  const [deleting, setDeleting] = useState(null);      // {property} | {properties: [...]}
  const [binKey, setBinKey] = useState(0);
  const checkedRows = useMemo(() => Object.values(checked), [checked]);
  const setFilters = (next) => patch(next);
  // Added-date bounds: repair a two-digit year ("0026-09-30" → "2026-09-30") and keep from <= to.
  const setDay = (key, raw) => {
    let v = raw || "";
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (m && Number(m[1]) < 100) v = `${2000 + Number(m[1])}-${m[2]}-${m[3]}`;
    const other = key === "added_from" ? "added_to" : "added_from";
    const next = { [key]: v };
    if (v && filters[other]) {
      if (key === "added_from" && v > filters[other]) next.added_to = v;
      if (key === "added_to" && v < filters[other]) next.added_from = v;
    }
    patch(next);
  };
  const applied = gate.applied || {};
  const active = FILTER_KEYS.filter((k) => applied[k]);
  const clearAll = () => { clear(); gate.reset(); setPage(1); setData(null); setSummary(null); };
  useEffect(() => { setPage(1); }, [gate.appliedKey]);

  // PIN jump deep-link (Phase 65): ?focus={propertyId} auto-opens the detail sheet.
  // The param itself is removed from the URL by useUrlFilters (strip) right after.
  useEffect(() => {
    const focus = searchParams.get("focus");
    if (focus) setSelected(focus);
  }, [searchParams]);

  const load = useCallback(async () => {
    if (!gate.applied) return;
    const params = { page, limit: 25 };
    Object.entries(gate.applied).forEach(([k, v]) => { if (v) params[k] = v; });
    setLoading(true);
    try {
      const r = await api.get(`/t/${code}/properties`, { params });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, [code, gate.appliedKey, page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 140: the six summary cards use exactly the applied filters (reloaded on every Fetch, not on paging)
  const loadSummary = useCallback(async () => {
    if (!gate.applied) return;
    const params = {};
    Object.entries(gate.applied).forEach(([k, v]) => { if (v) params[k] = v; });
    setSummaryLoading(true); setSummaryError(null);
    try {
      const r = await api.get(`/t/${code}/properties/summary`, { params });
      setSummary(r.data);
    } catch (e) {
      setSummaryError(e?.response?.data?.detail || "Could not load the summary");
    } finally {
      setSummaryLoading(false);
    }
  }, [code, gate.appliedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const live = useLiveCollections(code);   // Phase 149: collection status / summary cards follow the live ledger
  useEffect(() => { if (gate.fetched) load().catch(() => {}); }, [load, gate.fetched, gate.version, live]);
  useEffect(() => { if (gate.fetched) loadSummary(); }, [loadSummary, gate.fetched, gate.version, live]);
  const zoneName = namesOf(applied.zone_id, zones, "zone_name");
  const wardName = namesOf(applied.ward_id, wards, "ward_name");
  const mohallaName = namesOf(applied.mohalla_id, mohallas, "mohalla_name");
  const area = useMemo(() => ({ zoneId: filters.zone_id || "", wardId: filters.ward_id || "", mohallaId: filters.mohalla_id || "" }),
    [filters.zone_id, filters.ward_id, filters.mohalla_id]);
  const chips = gate.fetched ? [
    { key: "zone_id", label: "Zone", value: applied.zone_id ? (zoneName || "…") : "" },
    { key: "ward_id", label: "Ward", value: applied.ward_id ? (wardName || "…") : "" },
    { key: "mohalla_id", label: "Mohalla", value: applied.mohalla_id ? (mohallaName || "…") : "" },
    { key: "source", label: "Source", value: applied.source ? (SOURCE_META[applied.source]?.label || applied.source) : "" },
    { key: "payment_status", label: "Status", value: applied.payment_status },
    { key: "added_from", label: "Added from", value: applied.added_from ? fmtDay(applied.added_from) : "" },
    { key: "added_to", label: "Added to", value: applied.added_to ? fmtDay(applied.added_to) : "" },
    { key: "search", label: "Search", value: applied.search },
  ] : [];

  // Phase 192: after a delete, refresh the register + cards, clear the selection and bump the recycle-bin tab
  const afterDelete = () => {
    setChecked({});
    setBinKey((k) => k + 1);
    if (gate.fetched) { load().catch(() => {}); loadSummary(); }
  };
  const pageRows = data?.items || [];
  const pageSelectable = canDelete ? pageRows : [];
  const allOnPage = pageSelectable.length > 0 && pageSelectable.every((p) => checked[p.id]);
  const someOnPage = pageSelectable.some((p) => checked[p.id]);
  const toggleAll = (on) => setChecked((c) => {
    const next = { ...c };
    pageSelectable.forEach((p) => { if (on) next[p.id] = p; else delete next[p.id]; });
    return next;
  });
  const toggleOne = (p, on) => setChecked((c) => { const next = { ...c }; if (on) next[p.id] = p; else delete next[p.id]; return next; });
  const receiptsOf = (p) => Number(p.collection_summary?.payments_count || 0);

  return (
    <div>
      <PageHeader title="Properties" subtitle={view === "deleted" ? "Recycle bin — every deleted property with who deleted it, why and how; restorable by the Super Admin for 30 days"
        : !gate.fetched ? "Set your filters and click Fetch to load the register"
        : data == null ? "Loading register…"
        : active.length ? `${data.total.toLocaleString("en-IN")} ${data.total === 1 ? "property matches" : "properties match"} the current filters`
        : `${data.total.toLocaleString("en-IN")} properties in register`} />

      {canSeeDeleted && (
        <Tabs value={view} onValueChange={setView} className="mb-4">
          <TabsList data-testid="properties-tabs">
            <TabsTrigger value="register" className="gap-1.5" data-testid="properties-tab-register"><Building2 className="h-3.5 w-3.5" /> Register</TabsTrigger>
            <TabsTrigger value="deleted" className="gap-1.5" data-testid="properties-tab-deleted"><Trash2 className="h-3.5 w-3.5" /> Deleted properties</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {view === "deleted" ? (
        <DeletedPropertiesTab path={`/t/${code}/properties/deleted`} exportPath={`/t/${code}/properties/deleted/export`} codeOf={() => code} reloadKey={binKey + live} testId="deleted-properties" />
      ) : (
      <div data-testid="properties-register-view">
      <DrilldownBanner chips={chips} onClear={clearAll} testId="properties-drilldown" />
      <Card className="shadow-sm mb-4">
        <FetchForm onFetch={gate.fetch} testId="properties-filter-form">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search PIN, owner, house no, address, mobile..." value={filters.search}
              onChange={(e) => setFilters({ search: e.target.value })} data-testid="filter-search-input" />
          </div>
          <div className="w-full xl:w-[600px]">
            <TerritoryFilterSelects masters={masters} value={area} testIdPrefix="filter"
              onChange={(next) => setFilters({ zone_id: next.zoneId, ward_id: next.wardId, mohalla_id: next.mohallaId })} />
          </div>
          <Select value={filters.payment_status || "all"} onValueChange={(v) => setFilters({ payment_status: v === "all" ? "" : v })}>
            <SelectTrigger className="w-36" data-testid="filter-status-select"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {["pending", "partial", "paid"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.source || "all"} onValueChange={(v) => setFilters({ source: v === "all" ? "" : v })}>
            <SelectTrigger className="w-40" data-testid="filter-source-select"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {Object.entries(SOURCE_META).map(([k, m]) => (
                <SelectItem key={k} value={k} data-testid={`filter-source-option-${k}`}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Phase 140: date the property was added (IST business days, inclusive). A two-digit year typed into the
              native input ("0026-…") is read as 20xx on blur, and a reversed range pulls the other bound along. */}
          <div className="flex items-center gap-1.5" data-testid="filter-added-range">
            <span className="text-xs text-muted-foreground whitespace-nowrap flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Added</span>
            <Input type="date" className="h-10 w-[150px]" value={filters.added_from || ""} max={filters.added_to || undefined}
              onChange={(e) => setFilters({ added_from: e.target.value })}
              onBlur={(e) => setDay("added_from", e.target.value)} aria-label="Added from" data-testid="filter-added-from" />
            <span className="text-xs text-muted-foreground">to</span>
            <Input type="date" className="h-10 w-[150px]" value={filters.added_to || ""} min={filters.added_from || undefined}
              onChange={(e) => setFilters({ added_to: e.target.value })}
              onBlur={(e) => setDay("added_to", e.target.value)} aria-label="Added to" data-testid="filter-added-to" />
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <FetchDirtyHint dirty={gate.dirty} testId="properties-fetch-dirty" />
            <FetchButton type="submit" size="sm" loading={loading} dirty={gate.dirty} fetched={gate.fetched} testId="properties-fetch-button" />
            {access.featureAllowed("properties", "export") && <PropertyExportButton code={code} filters={applied} disabled={!gate.fetched} />}
            {FILTER_KEYS.some((k) => filters[k]) && (
              <Button type="button" variant="ghost" size="sm" className="h-10 text-muted-foreground" onClick={clearAll} data-testid="properties-clear-filters">
                <X className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
        </FetchForm>
      </Card>

      {gate.fetched && (
        <PropertyRegisterSummary data={summary} loading={summaryLoading} error={summaryError} onRetry={loadSummary}
          filtered={active.length > 0} />
      )}

      {!gate.fetched ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={gate.fetch} testId="properties-fetch-prompt"
            subtitle="Search by PIN, owner or address, or pick a zone, ward, status or source above — then click Fetch to load the register." />
        </CardContent></Card>
      ) : !data ? <Spinner /> : data.items.length === 0 ? (
        <EmptyState title="No properties found"
          subtitle={isAdmin && (applied.ward_id || applied.mohalla_id)
            ? "Nothing is linked to this ward / mohalla. If the Excel export does list properties here, their area ids were linked to a same-named ward or mohalla elsewhere — run the area link check to re-link them."
            : "Try adjusting filters and Fetch again, or upload demand data"}
          action={isAdmin && (applied.ward_id || applied.mohalla_id) ? (
            <Button variant="outline" onClick={() => setLinkageOpen(true)} data-testid="properties-check-area-links">
              <Link2 className="h-4 w-4 mr-2" /> Check area links
            </Button>
          ) : null} />
      ) : (
        <Card className="shadow-sm">
          <CardContent className="p-0">
            {/* Phase 192: bulk-delete action bar (only when rows are ticked) */}
            {canDelete && checkedRows.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b bg-destructive/5" data-testid="properties-bulk-bar">
                <Badge variant="secondary" className="tabular-nums" data-testid="properties-bulk-count">{checkedRows.length} selected</Badge>
                {checkedRows.some((p) => receiptsOf(p) > 0) && (
                  <span className="text-xs text-[hsl(var(--warning))] flex items-center gap-1" data-testid="properties-bulk-receipts-note">
                    <Receipt className="h-3.5 w-3.5" /> {checkedRows.filter((p) => receiptsOf(p) > 0).length} with receipts will be skipped
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="ghost" size="sm" className="h-8" onClick={() => setChecked({})} data-testid="properties-bulk-clear">
                    <X className="h-3.5 w-3.5 mr-1" /> Clear selection
                  </Button>
                  <Button variant="destructive" size="sm" className="h-8" onClick={() => setDeleting({ properties: checkedRows })} data-testid="properties-bulk-delete-button">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete selected ({checkedRows.length})
                  </Button>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <TooltipProvider delayDuration={200}>
              <Table data-testid="properties-table">
                <TableHeader>
                  <TableRow>
                    {canDelete && (
                      <TableHead className="w-10">
                        <Checkbox checked={allOnPage ? true : someOnPage ? "indeterminate" : false} onCheckedChange={(v) => toggleAll(!!v)}
                          aria-label="Select all properties on this page" data-testid="properties-select-all" />
                      </TableHead>
                    )}
                    <TableHead>PIN</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead className="hidden md:table-cell">Location</TableHead>
                    <TableHead className="text-right">Demand</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden lg:table-cell">Collector</TableHead>
                    <TableHead className="hidden xl:table-cell">Added</TableHead>
                    {canDelete && <TableHead className="w-10 text-right"><span className="sr-only">Delete</span></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((p) => (
                    <TableRow key={p.id} className={`hover:bg-muted/60 cursor-pointer ${checked[p.id] ? "bg-destructive/5" : ""}`} onClick={() => setSelected(p.id)}
                      data-testid={`property-row-${p.pin}`} data-selected={checked[p.id] ? "true" : undefined}>
                      {canDelete && (
                        <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={!!checked[p.id]} onCheckedChange={(v) => toggleOne(p, !!v)} aria-label={`Select ${p.pin}`}
                            data-testid={`property-check-${p.pin}`} />
                        </TableCell>
                      )}
                      <TableCell className="font-mono text-sm">
                        <span className="flex items-center gap-1.5">{p.pin}<SourceBadge source={p.source} /></span>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-sm">{p.owner_name}</p>
                        <p className="text-xs text-muted-foreground md:hidden">{p.ward_label}</p>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground"><MapPin className="h-3 w-3" />{p.zone_label} • {p.ward_label} • {p.mohalla_label || "-"}</span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{fmtINR(p.demand_summary.total)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{fmtINR(p.demand_summary.balance)}</TableCell>
                      <TableCell><StatusBadge status={p.payment_status} /></TableCell>
                      <TableCell className="hidden lg:table-cell text-sm">
                        {p.assignment ? (
                          <span className="flex items-center gap-1"><User className="h-3 w-3 text-muted-foreground" /><StaffLabel code={code} userId={p.assignment.tax_collector_id} name={p.assignment.collector_name} /></span>
                        ) : <span className="text-muted-foreground text-xs">Unassigned</span>}
                      </TableCell>
                      {/* Phase 140: when + by whom the property was added */}
                      <TableCell className="hidden xl:table-cell text-xs" data-testid={`property-added-${p.pin}`}>
                        <p className="tabular-nums" title={p.created_at ? `${fmtDateTime(p.created_at)} IST` : undefined}>{p.created_at ? fmtDateIST(p.created_at) : "—"}</p>
                        <p className="text-muted-foreground truncate max-w-[160px]">{p.created_by_name ? `by ${p.created_by_name}` : "—"}</p>
                      </TableCell>
                      {/* Phase 192: row delete (reason required; blocked by the server when receipts exist) */}
                      {canDelete && (
                        <TableCell className="text-right w-10" onClick={(e) => e.stopPropagation()}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setDeleting({ property: p })}
                                aria-label={`Delete ${p.pin}`} data-testid={`property-delete-${p.pin}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="text-xs max-w-[240px]">
                              {receiptsOf(p) > 0 ? `${BLOCKED_RECEIPTS_MSG} — ${receiptsOf(p)} receipt${receiptsOf(p) === 1 ? "" : "s"} on this property` : "Delete this property (reason required)"}
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </TooltipProvider>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-xs text-muted-foreground">Page {page} • {data.total} total</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1 || loading} onClick={() => setPage(page - 1)} data-testid="properties-prev-page">Previous</Button>
                <Button variant="outline" size="sm" disabled={page * 25 >= data.total || loading} onClick={() => setPage(page + 1)} data-testid="properties-next-page">Next</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      </div>
      )}

      <PropertyDetailSheet code={code} propId={selected} onClose={() => setSelected(null)} canDelete={canDelete} onDeleted={afterDelete} />
      {canDelete && (
        <DeletePropertyDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)} code={code}
          property={deleting?.property} properties={deleting?.properties} onDeleted={afterDelete} testId="property-delete" />
      )}
      {isAdmin && <AreaLinkageDialog code={code} open={linkageOpen} onOpenChange={setLinkageOpen} onDone={() => { if (gate.fetched) { load(); loadSummary(); } }} />}
    </div>
  );
}
